import { Component, h } from 'preact'
import Header from '../header'
import { GroupData, ImageData } from './Overview'
import GroupEditTabs from './GroupTabs'
import GroupInfoPanel from './GroupInfoPanel'
import DynamicSaveBar from '../ui-components/DynamicSaveBar'
import Axios from 'axios'
import AlertComponent from '../ui-components/AlertComponent'
import GroupFeaturedTopics, { featuredTopicsInfoType } from './GroupFeaturedTopics'
import GroupMembers from './GroupMembers'
import GroupApplications from './GroupApplications'
import ButtonControl from '../button-control/index'
import ConfirmationModal from '../ui-components/ConfirmationModal'

export type Member = {
  id: number,
  username: string,
  avatar: string,
  email: string,
  rank: string,
  applied_time?: string,
  member_since?: string,
  member_status: 'group_admin' | 'joined' | 'requested'
}

interface Props {
  group: GroupData,
  id: number,
  isSuccess: boolean,
  defaultCdn: string
}

interface State extends GroupData {
  src: null | string,
  crop: {
    x: number,
    y: number,
    width: number,
    height: number,
    aspect: number
  },
  pixelCrop: {
    width: number,
    height: number,
    x: number,
    y:number
  },
  isLoading: boolean,
  messageType: null | 'success' | 'error',
  message: null|string,
  titleError?: string,
  descriptionError?: string,
  activeTab: number,
  featuredTopicsInfo: undefined | featuredTopicsInfoType,
  showConfirmation: boolean,
  deleteInProgress: boolean
}

export class GroupEdit extends Component<Props, State> {
  constructor (props: Props) {
    super(props)

    const { title, description, heroImage, thumbnailImage, contentCuration, isPrivateGroup } = props.group

    const featuredTopicsInfo = {
      title: contentCuration ? contentCuration.title : '',
      featuredTopics: contentCuration ? contentCuration.curatedTopics : []
    }

    this.state = {
      title: title || '',
      description: description || '',
      heroImage: heroImage || null,
      thumbnailImage: thumbnailImage || null,
      src: null,
      crop: {
        x: 0,
        y: 0,
        width: 120,
        height: 60,
        aspect: 2
      },
      pixelCrop: {
        width: 120,
        height: 60,
        x: 0,
        y: 0
      },
      isLoading: false,
      messageType: null,
      message: null,
      titleError: '',
      descriptionError: '',
      activeTab: 0,
      featuredTopicsInfo: {
        title: featuredTopicsInfo ? featuredTopicsInfo.title : '',
        featuredTopics: featuredTopicsInfo ? featuredTopicsInfo.featuredTopics : []
      },
      showConfirmation: false,
      deleteInProgress: false
    }

    this.groupTabs = ['Group Details', 'Members']

    if (isPrivateGroup) {
      this.groupTabs.push('Applications')
    }

    this.savedGroupInfo = {
      title: title || '',
      description: description || '',
      heroImage: heroImage || null,
      featuredTopicsInfo: this.state.featuredTopicsInfo,
      thumbnailImage: thumbnailImage || null
    }

    let element: HTMLInputElement | null = document.querySelector('input#csrftoken')

    if (element !== null) {

      this.csrf = element.value

    }

    this.resetChanges = this.resetChanges.bind(this)
    this.onSave = this.onSave.bind(this)
    this.deleteCategory = this.deleteCategory.bind(this)
    this.getDeleteConfirmationTitle = this.getDeleteConfirmationTitle.bind(this)
    this.getDeleteConfirmationText = this.getDeleteConfirmationText.bind(this)
    this.getDeleteConfirmationButtons = this.getDeleteConfirmationButtons.bind(this)
    this.closeConfirmationDialog = this.closeConfirmationDialog.bind(this)
  }

  csrf: string = ''
  groupTabs: Array<string>
  savedGroupInfo: GroupData

  async onSave () {
    let errors: {
      title?: string,
      description?: string
    } = {}
    if (this.state.title === '') {
      errors.title = 'Please fill in this field'
    }

    if (this.state.description === '') {
      errors.description = 'Group description can’t be blank'
    }

    if (Object.keys(errors).length) {
      this.setState({
        titleError: errors.title || '',
        descriptionError: errors.description || ''
      })

      return
    }

    this.setState({ isLoading: true, messageType: null })
    const { title, description, heroImage, thumbnailImage, featuredTopicsInfo } = this.state
    try {
      await Axios.post(`/platform/group/${this.props.id}`, {
        title: title,
        description: description,
        heroImage: heroImage,
        thumbnailImage: thumbnailImage,
        groupType: this.props.group.type,
        contentCuration: {
          title: this.state.featuredTopicsInfo?.title,
          curatedTopics: this.state.featuredTopicsInfo?.featuredTopics.map(featuredTopic => ({
            id: featuredTopic.id,
            type: featuredTopic.contentType
          }))
        }
      })

      this.savedGroupInfo = {
        title,
        description,
        heroImage,
        featuredTopicsInfo,
        thumbnailImage
      }

      this.setState({ isLoading: false, messageType: 'success', message: null })
    } catch (err) {
      this.setState({
        isLoading: false,
        messageType: 'error',
        message: null
      })
    }
  }

  async deleteCategory () {
    this.setState({ deleteInProgress: true })
    await Axios.delete(`/platform/group/${this.props.id}`, { data: { csrfToken: this.csrf } })
    window.location.assign('/platform/groups')
  }

  canBeDeleted () {
    return this.props.group.visibleTopics === 0
  }

  closeConfirmationDialog () {
    this.setState({ showConfirmation: false })
  }

  getDeleteConfirmationTitle () {
    if (this.canBeDeleted()) {
      return 'Delete group?'
    }

    return 'Group is not empty yet'
  }

  getDeleteConfirmationText () {
    if (this.canBeDeleted()) {
      return "Be careful, once it's deleted, it's gone for good!"
    }

    return <div>
      If you're absolutely sure you want to delete it, you'll need to move all of its <strong>{this.props.group.visibleTopics} topics</strong> a different category/group or delete them.
    </div>
  }

  getDeleteConfirmationButtons () {
    if (!this.canBeDeleted()) {
      return [{
        text: 'Okay',
        callback: this.closeConfirmationDialog,
        class: 'btn btn-primary',
        showLoading: false
      }]
    }
    return [
      {
        text: 'Delete',
        callback: this.deleteCategory,
        class: 'btn btn-danger',
        showLoading: true
      },
      {
        text: 'Cancel',
        callback: this.closeConfirmationDialog,
        class: 'btn btn-default',
        showLoading: false
      }
    ]
  }

  resetChanges () {
    this.setState({
      title: this.savedGroupInfo.title,
      description: this.savedGroupInfo.description,
      heroImage: this.savedGroupInfo.heroImage,
      featuredTopicsInfo: this.savedGroupInfo.featuredTopicsInfo
    })
  }

  componentDidMount () {
    if (this.props.isSuccess) {
      this.setState({ messageType: 'success', message: 'New group is added successfully!' })
    }
  }

  render ({ id, defaultCdn }: Props, {
    title,
    description,
    heroImage,
    thumbnailImage,
    isLoading,
    messageType,
    message,
    titleError,
    descriptionError,
    activeTab,
    featuredTopicsInfo,
    showConfirmation,
    deleteInProgress
  }: State) {
    const displaySaveBar = this.savedGroupInfo.title !== title ||
      this.savedGroupInfo.description !== description ||
      JSON.stringify(this.savedGroupInfo.heroImage) !== JSON.stringify(heroImage) ||
      JSON.stringify(this.savedGroupInfo.thumbnailImage) !== JSON.stringify(thumbnailImage) ||
      JSON.stringify(this.savedGroupInfo.featuredTopicsInfo) !== JSON.stringify(featuredTopicsInfo)

    return (
      <div className='group-edit-page'>
        <Header
          title='Edit group'
          disableCancelButton
          disableSaveButton
          hasBreadcrumb
          breadcrumbText='Back to group overview'
          breadcrumbLink='/platform/groups'
        />
        { messageType ? <AlertComponent sticky messageType={messageType} message={message} isVisibleFor={3000} /> : null }
        <GroupEditTabs
          tabs={this.groupTabs}
          activeTab={activeTab}
          onCallback={(activeTab: number) => this.setState({ activeTab: activeTab })}
        />
        {
          this.groupTabs[activeTab] === 'Group Details' ? (
            <div className='group-edit-page_details-tab'>
              <GroupInfoPanel
                title={title}
                updateTitle={(title: string) => this.setState({ title: title, titleError: '' })}
                description={description}
                updateDescription={(description: string) => this.setState({ description: description, descriptionError: '' })}
                heroImage={heroImage}
                thumbnailImage={thumbnailImage}
                updateHeroImage={(heroImage: ImageData|null) => this.setState({ heroImage: heroImage })}
                updateThumbnailImage={(thumbnailImage: ImageData|null) => this.setState({ thumbnailImage: thumbnailImage })}
                titleError={titleError}
                descriptionError={descriptionError}
              />
              <GroupFeaturedTopics
                title={featuredTopicsInfo ? featuredTopicsInfo.title : ''}
                featuredTopics={featuredTopicsInfo ? featuredTopicsInfo.featuredTopics : []}
                onCallback={(featuredTopicsInfo: featuredTopicsInfoType) => this.setState({ featuredTopicsInfo: featuredTopicsInfo })}
              />

              <ButtonControl title='Delete this group' variant='danger' onClick={() => this.setState({ showConfirmation: true })} />
              <ConfirmationModal
                isLoading={deleteInProgress}
                enableModal={showConfirmation}
                title={this.getDeleteConfirmationTitle()}
                message={this.getDeleteConfirmationText()}
                buttons={this.getDeleteConfirmationButtons()}
                onClose={this.closeConfirmationDialog}
              />
            </div>
          ) : null
        }

        {
          this.groupTabs[activeTab] === 'Members' ? (
            <GroupMembers groupId={id} groupName={title} />
          ) : null
        }
        {
          this.groupTabs[activeTab] === 'Applications' ? (
            <GroupApplications groupId={id} defaultCdn={defaultCdn} />
          ) : null
        }

        {
          displaySaveBar ? (
            <DynamicSaveBar
              isLoading={isLoading}
              cancelCallback={this.resetChanges}
              saveCallback={this.onSave} />
          ) : null
        }
      </div>
    )
  }
}

export default GroupEdit
